import { toast, Toast } from '@heroui/react'
import { Link, router, usePage } from '@inertiajs/react'
import { type ReactNode } from 'react'

import { currentNavItem, NAV_ITEMS } from '@/lib/nav'
import { APP_NAME } from '@/lib/pageTitle'

// One toast per response carrying a flash, even with the same text. HeroUI pauses the timer on hover and focus.
// Module scope, not an effect: on a full page load, Inertia may fire `flash` before the layout's effects run.
const stopFlashToasts = router.on('flash', ({ detail: { flash } }) => {
  if (!location.pathname.startsWith('/dashboard')) return // The login page shows its alert inline.
  if (flash.alert) toast.danger(flash.alert, { timeout: 10000 })
  else if (flash.notice) toast.success(flash.notice, { timeout: 5000 })
})
import.meta.hot?.dispose(stopFlashToasts)

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { props, url } = usePage()
  const current = currentNavItem(url)

  return (
    <div className="min-h-screen">
      {/* Bottom of the screen: the header and its navigation stay uncovered. */}
      <Toast.Provider placement="bottom" />
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-5xl items-center gap-6 px-6 py-4">
          {/* The landing's wordmark: a thick mail-yellow underline running through the letters. */}
          <Link
            href="/dashboard"
            className="text-xl font-semibold tracking-tight underline decoration-brand decoration-[0.2em] underline-offset-[0.12em] [text-decoration-skip-ink:none]"
          >
            {APP_NAME}
          </Link>

          <nav className="flex gap-4" aria-label="Main">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                aria-current={item === current ? 'page' : undefined}
                className={item === current ? 'font-medium' : 'text-muted hover:text-foreground'}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-4 text-sm">
            <span data-testid="current-user-email">{props.auth.user?.email}</span>
            <Link href="/logout" method="delete" as="button" className="text-muted hover:text-foreground">
              Sign out
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        {children}
      </main>
    </div>
  )
}
